import ContactPreviewTable from '@/components/ContactPreviewTable';
import TemplatePreview from '@/components/TemplatePreview';
import { useToast } from '@/hooks/useToast';
import { CreateBroadcastSchema } from '@/schemas/broadcast';
import { broadcastsService } from '@/services/broadcastsService';
import { mediaService } from '@/services/mediaService';
import { projectContactsService } from '@/services/projectContactsService';
import { templatesService } from '@/services/templatesService';
import { whatsappConfigService } from '@/services/whatsappConfigService';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import {
  BarChart2,
  ChevronLeft,
  Clock,
  Info,
  MessageSquare,
  Play,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';

/**
 * BroadcastCreate page component for creating new broadcasts
 */
const BroadcastCreate = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // Dynamic parameters state (these are temporary UI states to build the payload)
  const [headerParams, setHeaderParams] = useState({});
  const [bodyParams, setBodyParams] = useState({});
  const [buttonParams, setButtonParams] = useState({});
  const [headerFile, setHeaderFile] = useState(null);

  // react-hook-form setup
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateBroadcastSchema),
    defaultValues: {
      name: '',
      description: '',
      templateId: '',
      executionType: 'immediate',
      scheduledAt: '',
      contactSelectionMethod: 'uploaded',
      saveContacts: false,
      contacts: [],
      filters: [{ field: '', operator: '', value: '' }],
      filterLogic: 'AND',
    },
    mode: 'onChange',
  });

  const {
    fields: filterFields,
    append: appendFilter,
    remove: removeFilter,
  } = useFieldArray({
    control,
    name: 'filters',
  });

  const { remove: removeContact, replace: replaceContacts } = useFieldArray({
    control,
    name: 'contacts',
  });

  // Watchers
  const watchedContactMethod = watch('contactSelectionMethod');
  const watchedExecutionType = watch('executionType');
  const watchedTemplateId = watch('templateId');
  const watchedFilterLogic = watch('filterLogic');
  const watchedContacts = watch('contacts');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isParsing, setIsParsing] = useState(false);

  // Fetch approved templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates-approved', projectId],
    queryFn: () =>
      templatesService.getTemplates(projectId, {
        limit: 100,
      }),
    enabled: !!projectId,
  });

  // Fetch WhatsApp configuration
  const { data: whatsappConfigData } = useQuery({
    queryKey: ['whatsapp-config', projectId],
    queryFn: () => whatsappConfigService.getWhatsAppConfig(projectId),
    enabled: !!projectId,
  });

  const whatsappConfig = whatsappConfigData?.data?.whatsappConfig;
  const phoneNumberId = whatsappConfig?.phoneNumberId;
  const templates = useMemo(
    () => templatesData?.docs || [],
    [templatesData?.docs]
  );

  // Parse template components to extract dynamic parameters
  const parseTemplateComponents = useCallback(
    template => {
      setHeaderParams({});
      setBodyParams({});
      setButtonParams({});

      if (template.header) {
        if (template.header.text) {
          const matches = template.header.text.match(/\{\{(\d+)\}\}/g);
          if (matches) {
            const params = {};
            matches.forEach(match => {
              const index = match.replace(/[{}]/g, '');
              params[index] = '';
            });
            setHeaderParams(params);
          }
        }
      }

      if (template.body?.text) {
        const matches = template.body.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          const params = {};
          matches.forEach(match => {
            const index = match.replace(/[{}]/g, '');
            params[index] = '';
          });
          setBodyParams(params);
        }
      }

      if (
        template.buttons &&
        Array.isArray(template.buttons) &&
        template.buttons.length > 0
      ) {
        const params = {};
        template.buttons.forEach((button, btnIndex) => {
          if (button.type === 'copy_code') {
            params[`copy_code_${btnIndex}`] = '';
          }
          if (
            button.type === 'url' &&
            (button.url?.includes('{{') ||
              (button.example && button.example[0]))
          ) {
            params[`url_${btnIndex}`] = '';
          }
        });

        if (Object.keys(params).length > 0) {
          setButtonParams(params);
        }
      }
    },
    [setHeaderParams, setBodyParams, setButtonParams]
  );

  // Handle template selection
  const handleTemplateChange = useCallback(
    templateId => {
      const template = templates.find(t => t._id === templateId);
      setValue('templateId', templateId);
      setSelectedTemplate(template);

      setHeaderParams({});
      setBodyParams({});
      setButtonParams({});
      setHeaderFile(null);

      if (template) {
        parseTemplateComponents(template);
      }
    },
    [templates, parseTemplateComponents, setValue]
  );

  // Handle file upload for header media
  const handleHeaderFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      const header = selectedTemplate?.header;
      if (header && header.format) {
        const { format } = header;
        let valid = false;
        let maxSize = 0;

        if (format === 'IMAGE') {
          valid = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
          maxSize = 5 * 1024 * 1024;
        } else if (format === 'VIDEO') {
          valid = ['video/mp4', 'video/3gpp'].includes(file.type);
          maxSize = 16 * 1024 * 1024;
        } else if (format === 'DOCUMENT') {
          valid = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          ].includes(file.type);
          maxSize = 100 * 1024 * 1024;
        }

        if (!valid) {
          showError(`Invalid file type for ${format}`);
          return;
        }

        if (file.size > maxSize) {
          showError(
            `File size exceeds ${(maxSize / 1024 / 1024).toFixed(0)}MB limit`
          );
          return;
        }

        setHeaderFile(file);
      }
    }
  };

  // Handle contacts file upload and parsing
  const handleContactsFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      setIsParsing(true);
      reader.onload = event => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Map headers (case insensitive) to name and phoneNumber
          const processedContacts = jsonData.map(row => {
            const keys = Object.keys(row);
            const nameKey = keys.find(k => k.toLowerCase().includes('name'));
            const phoneKey = keys.find(
              k =>
                k.toLowerCase().includes('phone') ||
                k.toLowerCase().includes('number') ||
                k.toLowerCase().includes('mobile') ||
                k.toLowerCase().includes('contact')
            );

            return {
              name: (nameKey ? row[nameKey] : '').toString().trim(),
              phoneNumber: (phoneKey ? row[phoneKey] : '')
                .toString()
                .trim()
                .replace(/\D/g, ''),
            };
          });

          replaceContacts(processedContacts);
          showSuccess(
            `Successfully imported ${processedContacts.length} contacts`
          );
        } catch (error) {
          console.error('File parsing error:', error);
          showError(
            'Failed to parse file. Please ensure it is a valid Excel/CSV file.'
          );
        } finally {
          setIsParsing(false);
          // Reset file input so same file can be uploaded again if needed
          e.target.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // Handle download sample template
  const handleDownloadTemplate = async () => {
    try {
      const blob = await projectContactsService.getContactImportTemplate();
      saveAs(blob, 'contacts-import-template.xlsx');
      showSuccess('Sample template downloaded successfully');
    } catch (error) {
      console.error('Failed to download template:', error);
      showError('Failed to download template. Please try again.');
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async payload => {
      return await broadcastsService.createBroadcast(payload);
    },
    onSuccess: data => {
      showSuccess(data.message || 'Broadcast created successfully');
      queryClient.invalidateQueries(['broadcasts', projectId]);
      navigate(`/projects/${projectId}/broadcasts`);
    },
    onError: error => {
      console.error('Error creating broadcast:', error);
      showError(error?.message || 'Failed to create broadcast');
      setIsSubmitting(false);
    },
  });

  const onFormSubmit = async data => {
    setIsSubmitting(true);
    try {
      let headerMediaId = null;
      let key = null;
      if (headerFile) {
        if (!phoneNumberId) {
          throw new Error(
            'WhatsApp configuration (Phone Number ID) is missing.'
          );
        }
        const mediaResponse = await mediaService.uploadWhatsAppMedia(
          projectId,
          phoneNumberId,
          headerFile
        );
        headerMediaId = mediaResponse?.data?.id;
        key = mediaResponse?.data?.key;
        if (!headerMediaId || !key) {
          throw new Error('Failed to upload media to WhatsApp');
        }
      }

      const template = selectedTemplate;
      const dynamicValues = { header: {}, body: [], buttons: [] };

      if (template?.header) {
        const { format } = template.header;
        if (format === 'TEXT' && Object.keys(headerParams).length > 0) {
          const val = headerParams['1'] || '';
          dynamicValues.header = { value: val, fallback: val };
        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
          dynamicValues.header = { url: headerMediaId, key };
        }
      }

      Object.keys(bodyParams)
        .sort((a, b) => Number(a) - Number(b))
        .forEach(paramsKey => {
          dynamicValues.body.push({
            value: bodyParams[paramsKey],
            fallback: bodyParams[paramsKey],
          });
        });

      Object.keys(buttonParams).forEach(paramsKey => {
        const parts = paramsKey.split('_');
        const index = Number(parts[parts.length - 1]);
        const type = parts.slice(0, -1).join('_');
        dynamicValues.buttons.push({
          index,
          value: buttonParams[paramsKey],
          type,
        });
      });

      const payload = {
        name: data.name.trim(),
        description: data.description?.trim() || '',
        templateId: data.templateId,
        dynamicValues,
        contactSelectionMethod: data.contactSelectionMethod,
        executionType: data.executionType,
        projectId,
      };

      if (data.contactSelectionMethod === 'filtered') {
        payload.contactSelectionCriteria = {
          logic: data.filterLogic,
          filters: data.filters.map(f => ({
            field: f.field,
            operator: f.operator,
            value: f.value,
          })),
        };
      }

      if (data.executionType === 'scheduled' && data.scheduledAt) {
        payload.scheduledAt = data.scheduledAt;
      }

      if (data.contactSelectionMethod === 'uploaded') {
        payload.contacts = data.contacts;
        await createMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
    } catch (error) {
      console.error('Error creating broadcast:', error);
      setIsSubmitting(false);
    }
  };

  const renderHeaderMediaInput = () => {
    const header = selectedTemplate?.header;
    if (!header || !header.format || header.format === 'TEXT') return null;
    const { format } = header;
    const accept =
      format === 'IMAGE'
        ? 'image/jpeg,image/jpg,image/png'
        : format === 'VIDEO'
          ? 'video/mp4,video/3gpp'
          : 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
    const label = `Header ${format.charAt(0) + format.slice(1).toLowerCase()}`;
    const maxSize =
      format === 'IMAGE' ? '5MB' : format === 'VIDEO' ? '16MB' : '100MB';

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {label} <span className="text-red-500">*</span>
        </label>
        <input
          type="file"
          accept={accept}
          onChange={handleHeaderFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
        />
        {headerFile && (
          <div className="flex items-center text-sm text-green-600 font-medium">
            <span className="mr-2">✓</span>
            {headerFile.name}
          </div>
        )}
        <p className="text-[10px] text-gray-400 font-medium italic">
          Max size: {maxSize}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-full bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-20">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={`/projects/${projectId}/broadcasts`}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <BarChart2 className="w-6 h-6 mr-2 text-primary-600" />
                  Create Broadcast
                </h1>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                  Campaign Settings
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Form Section */}
          <div className="flex-1 w-full order-2 lg:order-1">
            <form
              onSubmit={handleSubmit(onFormSubmit)}
              className="space-y-8 pb-12"
              noValidate
            >
              {/* Main Configuration Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight flex items-center">
                    <Info className="w-4 h-4 mr-2 text-primary-500" />
                    Basic Information
                  </h3>
                </div>
                <div className="p-6 space-y-6">
                  <div data-error={errors.name?.message}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Broadcast Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register('name')}
                      placeholder="e.g., Summer Sale 2026"
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all ${errors.name ? 'border-red-300 bg-red-50/10' : 'border-gray-300'}`}
                      disabled={isSubmitting}
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs font-medium text-red-600">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      {...register('description')}
                      placeholder="Brief context about this broadcast..."
                      rows={2}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all resize-none"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Template Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2 text-blue-500" />
                    Template Selection
                  </h3>
                </div>
                <div className="p-6 space-y-6">
                  <div data-error={errors.templateId?.message}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Choose Template <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={watchedTemplateId}
                      onChange={e => handleTemplateChange(e.target.value)}
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all appearance-none bg-no-repeat bg-[right_1rem_center] ${errors.templateId ? 'border-red-300 bg-red-50/10' : 'border-gray-300'}`}
                      disabled={isSubmitting || templatesLoading}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundSize: '1.25rem',
                      }}
                    >
                      <option value="">
                        {templatesLoading
                          ? 'Loading templates...'
                          : 'Select an approved template'}
                      </option>
                      {templates.map(template => (
                        <option key={template._id} value={template._id}>
                          {template.name} ({template.language || 'en'})
                        </option>
                      ))}
                    </select>
                    {errors.templateId && (
                      <p className="mt-1 text-xs font-medium text-red-600">
                        {errors.templateId.message}
                      </p>
                    )}
                  </div>

                  {selectedTemplate && (
                    <div className="space-y-6 pt-4 border-t border-gray-100">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Template Personalization
                      </h4>

                      {/* Header Params */}
                      {((selectedTemplate.header?.format &&
                        selectedTemplate.header.format !== 'TEXT') ||
                        Object.keys(headerParams).length > 0) && (
                        <div className="p-5 bg-amber-50/50 border border-amber-100 rounded-xl space-y-4">
                          <div className="flex items-center text-amber-800 space-x-2">
                            <span className="font-bold text-[10px] bg-amber-100 px-2 py-0.5 rounded-full uppercase">
                              Header
                            </span>
                          </div>
                          {renderHeaderMediaInput()}
                          {Object.keys(headerParams).map(key => (
                            <div key={key}>
                              <label className="block text-xs font-bold text-amber-900 mb-1.5 uppercase tracking-tight">
                                Param &#123;&#123;{key}&#125;&#125;
                              </label>
                              <input
                                type="text"
                                value={headerParams[key]}
                                onChange={e =>
                                  setHeaderParams(prev => ({
                                    ...prev,
                                    [key]: e.target.value,
                                  }))
                                }
                                placeholder={`Value for {{${key}}}`}
                                className="w-full px-4 py-2.5 rounded-lg border border-amber-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
                                disabled={isSubmitting}
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Body Params */}
                      {Object.keys(bodyParams).length > 0 && (
                        <div className="p-5 bg-indigo-50/50 border border-indigo-100 rounded-xl space-y-4">
                          <div className="flex items-center text-indigo-800 space-x-2">
                            <span className="font-bold text-[10px] bg-indigo-100 px-2 py-0.5 rounded-full uppercase">
                              Message Body
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {Object.keys(bodyParams).map(key => (
                              <div key={key}>
                                <label className="block text-xs font-bold text-indigo-900 mb-1.5 uppercase tracking-tight">
                                  Variable &#123;&#123;{key}&#125;&#125;
                                </label>
                                <input
                                  type="text"
                                  value={bodyParams[key]}
                                  onChange={e =>
                                    setBodyParams(prev => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  placeholder={`Value for {{${key}}}`}
                                  className="w-full px-4 py-2.5 rounded-lg border border-indigo-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                                  disabled={isSubmitting}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Button Params */}
                      {Object.keys(buttonParams).length > 0 && (
                        <div className="p-5 bg-emerald-50/50 border border-emerald-100 rounded-xl space-y-4">
                          <div className="flex items-center text-emerald-800 space-x-2">
                            <span className="font-bold text-[10px] bg-emerald-100 px-2 py-0.5 rounded-full uppercase">
                              Buttons
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {Object.keys(buttonParams).map(key => (
                              <div key={key}>
                                <label className="block text-xs font-bold text-emerald-900 mb-1.5 uppercase tracking-tight">
                                  {key.startsWith('copy_code')
                                    ? 'Coupon Code'
                                    : 'URL Suffix'}
                                </label>
                                <input
                                  type="text"
                                  value={buttonParams[key]}
                                  onChange={e =>
                                    setButtonParams(prev => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  placeholder="Enter value"
                                  className="w-full px-4 py-2.5 rounded-lg border border-emerald-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                  disabled={isSubmitting}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Audience Card */}
              {selectedTemplate && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-200">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight flex items-center">
                      <Users className="w-4 h-4 mr-2 text-indigo-500" />
                      Target Audience
                    </h3>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 gap-4">
                      {/* Upload */}
                      <label
                        className={`relative flex flex-col p-5 border-2 rounded-xl cursor-pointer transition-all ${watchedContactMethod === 'uploaded' ? 'border-primary-500 bg-primary-50/30' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              {...register('contactSelectionMethod')}
                              value="uploaded"
                              className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                            />
                            <span className="ml-3 font-semibold text-gray-900">
                              Import from Excel/CSV
                            </span>
                          </div>
                        </div>
                        {watchedContactMethod === 'uploaded' && (
                          <div
                            className="ml-7 space-y-4 pt-2 cursor-default"
                            onClick={e => e.stopPropagation()}
                          >
                            <div className="flex items-center space-x-4">
                              <label className="flex-1">
                                <span className="sr-only">Choose file</span>
                                <input
                                  type="file"
                                  accept=".xlsx,.xls,.csv"
                                  onChange={handleContactsFileChange}
                                  className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-primary-600 file:text-white hover:file:bg-primary-700 cursor-pointer disabled:opacity-50"
                                  disabled={isParsing}
                                />
                              </label>
                              <button
                                type="button"
                                onClick={handleDownloadTemplate}
                                className="text-[10px] font-bold text-primary-600 uppercase tracking-wider hover:underline whitespace-nowrap"
                              >
                                Download Sample
                              </button>
                            </div>

                            {isParsing && (
                              <div className="flex items-center text-sm text-primary-600 animate-pulse">
                                <Clock className="w-4 h-4 mr-2 animate-spin" />
                                Parsing file...
                              </div>
                            )}

                            {watchedContacts.length > 0 && (
                              <div className="space-y-4">
                                <ContactPreviewTable
                                  contacts={watchedContacts}
                                  onUpdate={(index, data) =>
                                    setValue(`contacts.${index}`, data, {
                                      shouldValidate: true,
                                    })
                                  }
                                  onRemove={removeContact}
                                />
                                <div className="flex items-center text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                                  <Info className="w-4 h-4 mr-2 text-blue-500" />
                                  Review and fix any invalid contacts above
                                  before submitting.
                                </div>
                              </div>
                            )}

                            {/* <div className="flex items-center space-x-2 pt-2">
                              <input
                                type="checkbox"
                                {...register('saveContacts')}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <span className="text-xs text-gray-600 font-medium">
                                Save these contacts to the project database
                              </span>
                            </div> */}

                            {errors.contacts && (
                              <p className="text-sm text-red-600 font-medium">
                                {errors.contacts.message}
                              </p>
                            )}
                          </div>
                        )}
                      </label>

                      {/* Filtered */}
                      <label
                        className={`relative flex flex-col p-5 border-2 rounded-xl cursor-pointer transition-all ${watchedContactMethod === 'filtered' ? 'border-primary-500 bg-primary-50/30' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <div className="flex items-center mb-2">
                          <input
                            type="radio"
                            {...register('contactSelectionMethod')}
                            value="filtered"
                            className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                          />
                          <span className="ml-3 font-semibold text-gray-900">
                            Filter Existing Contacts
                          </span>
                        </div>

                        {watchedContactMethod === 'filtered' && (
                          <div
                            className="ml-7 space-y-4 pt-2 cursor-default"
                            onClick={e => e.stopPropagation()}
                          >
                            <div className="flex items-center space-x-4 mb-4">
                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                Logic:
                              </span>
                              <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button
                                  type="button"
                                  onClick={() => setValue('filterLogic', 'AND')}
                                  className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${watchedFilterLogic === 'AND' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  AND
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setValue('filterLogic', 'OR')}
                                  className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${watchedFilterLogic === 'OR' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  OR
                                </button>
                              </div>
                            </div>

                            <div className="space-y-3">
                              {filterFields.map((field, index) => (
                                <div
                                  key={field.id}
                                  className="group relative flex flex-wrap sm:flex-nowrap items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl transition-all hover:border-gray-300"
                                >
                                  <div className="flex-1 min-w-[120px]">
                                    <select
                                      {...register(`filters.${index}.field`)}
                                      className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none"
                                    >
                                      <option value="">Field</option>
                                      <option value="name">Name</option>
                                      <option value="phoneNumber">
                                        Phone Number
                                      </option>
                                      <option value="tags">Tags</option>
                                      <option value="createdAt">
                                        Created Date
                                      </option>
                                    </select>
                                  </div>
                                  <div className="flex-1 min-w-[120px]">
                                    <select
                                      {...register(`filters.${index}.operator`)}
                                      className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none"
                                    >
                                      <option value="">Condition</option>
                                      <option value="equals">Equals</option>
                                      <option value="contains">Contains</option>
                                      <option value="startsWith">
                                        Starts With
                                      </option>
                                      <option value="in">
                                        In (comma separated)
                                      </option>
                                    </select>
                                  </div>
                                  <div className="flex-[2] min-w-[150px]">
                                    <input
                                      type="text"
                                      {...register(`filters.${index}.value`)}
                                      placeholder="Value"
                                      className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeFilter(index)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                    title="Remove filter"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                appendFilter({
                                  field: '',
                                  operator: '',
                                  value: '',
                                })
                              }
                              className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/50 transition-all flex items-center justify-center group"
                            >
                              <Plus className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                              Add Filter Rule
                            </button>

                            {errors.filters && (
                              <p className="text-sm text-red-600 font-medium">
                                {errors.filters.message}
                              </p>
                            )}
                          </div>
                        )}
                      </label>

                      {/* All */}
                      <label
                        className={`relative flex items-center p-5 border-2 rounded-xl cursor-pointer transition-all ${watchedContactMethod === 'all' ? 'border-primary-500 bg-primary-50/30' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <input
                          type="radio"
                          {...register('contactSelectionMethod')}
                          value="all"
                          className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                        />
                        <div className="ml-3">
                          <span className="block font-semibold text-gray-900">
                            All Contacts
                          </span>
                          <span className="block text-xs text-gray-500">
                            Reach everyone in this project
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Execution Card */}
              {selectedTemplate && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-200">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight flex items-center">
                      <Play className="w-4 h-4 mr-2 text-primary-500" />
                      Launch Schedule
                    </h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label
                        className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${watchedExecutionType === 'immediate' ? 'border-primary-500 bg-primary-50/30' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <input
                          type="radio"
                          {...register('executionType')}
                          value="immediate"
                          className="w-4 h-4"
                        />
                        <div className="ml-3">
                          <span className="block text-sm font-bold text-gray-900">
                            Immediate
                          </span>
                          <span className="block text-[10px] text-gray-500 font-medium uppercase mt-0.5">
                            Start sending now
                          </span>
                        </div>
                      </label>
                      <label
                        className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${watchedExecutionType === 'scheduled' ? 'border-primary-500 bg-primary-50/30' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <input
                          type="radio"
                          {...register('executionType')}
                          value="scheduled"
                          className="w-4 h-4"
                        />
                        <div className="ml-3">
                          <span className="block text-sm font-bold text-gray-900">
                            Scheduled
                          </span>
                          <span className="block text-[10px] text-gray-500 font-medium uppercase mt-0.5">
                            Pick a future date
                          </span>
                        </div>
                      </label>
                    </div>

                    {watchedExecutionType === 'scheduled' && (
                      <div
                        className="pt-4 animate-in fade-in slide-in-from-top-2 duration-300"
                        data-error={errors.scheduledAt?.message}
                      >
                        <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                          Select Date & Time{' '}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="datetime-local"
                            {...register('scheduledAt')}
                            className={`w-full pl-11 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all ${errors.scheduledAt ? 'border-red-300 bg-red-50/10' : 'border-gray-300'}`}
                          />
                        </div>
                        {errors.scheduledAt ? (
                          <p className="mt-1.5 text-xs font-medium text-red-600">
                            {errors.scheduledAt.message}
                          </p>
                        ) : (
                          <p className="mt-1.5 text-[10px] text-gray-400 font-medium italic">
                            Must be at least 20 mins from now
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate(`/projects/${projectId}/broadcasts`)}
                  disabled={isSubmitting}
                  className="px-8 py-3 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || templatesLoading}
                  className="px-10 py-3 rounded-lg text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 hover:shadow-lg hover:shadow-primary-600/20 active:scale-[0.98] transition-all disabled:opacity-50 inline-flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Processing...
                    </>
                  ) : watchedExecutionType === 'scheduled' ? (
                    'Schedule Broadcast'
                  ) : (
                    'Launch Campaign'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="lg:w-96 w-full lg:sticky lg:top-24 order-1 lg:order-2">
            <TemplatePreview
              template={selectedTemplate}
              headerParams={headerParams}
              bodyParams={bodyParams}
              buttonParams={buttonParams}
              headerFile={headerFile}
              showMobilePreview={true}
              businessName={whatsappConfig?.verifiedName}
              businessLogo={whatsappConfig?.profilePictureUrl}
              className="scale-[0.9] origin-top"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastCreate;
